import { Icon, ICONS } from '@/common/components/Icon';
import { GoalVisibilityFilter } from '@/common/constants/filter';
import { ActiveStatusCodes, StatusCode } from '@/common/constants/status';
import {
    GoalWithInfos,
    Project,
    UserWithDisplaySettings,
} from '@/common/types/databaseTypes';
import {
    useMultiTeamGoals,
    useUserGoals,
    useGoalById,
} from '@/features/goal/services/useGoalQueries';
import { useMyTeams } from '@/features/team/services/useTeamQueries';
import { useAuthStore } from '@/stores/authStore';
import { useTodoDrawerStore } from '@/stores/todoDrawerStore';
import {
    Accordion,
    ActionIcon,
    Box,
    Button,
    Group,
    Loader,
    Menu,
    Pill,
    ScrollArea,
    Stack,
    Text,
    Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createStyles } from '@mantine/styles';
import {
    IconCheck,
    IconChevronLeft,
    IconExternalLink,
    IconList,
    IconSwipe,
} from '@tabler/icons-react';
import { t } from 'i18next';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useUpdateTodo } from '../services/useTodoQueries';

// 타입 정의
interface GoalSelectorProps {
    todoId?: string;
    goalId?: string | null;
    customButton?: React.ReactNode;
    onGoalChange?: (goalId: string | null) => void;
    goalVisibilityFilter?: GoalVisibilityFilter;
    withinPortal?: boolean; // default true
}

interface ProjectGoalGroup {
    project: Project;
    goals: Partial<GoalWithInfos>[];
}

interface GoalTreeProps {
    goals: Partial<GoalWithInfos>[];
    currentGoalId: string | null | undefined;
    handleSelectGoal: (goalId: string | null) => Promise<void>;
    subGoalItemClass: string;
}

interface ProjectAccordionProps {
    projectGoals: ProjectGoalGroup[];
    activeProjectId: string | null;
    renderGoals: (
        goals: Partial<GoalWithInfos>[] | undefined
    ) => React.ReactNode;
}

// 스타일 정의
const useStyles = createStyles(theme => ({
    subGoalItem: {
        marginLeft: theme.spacing.md,
    },
    header: {
        position: 'sticky',
        top: 0,
        background: theme.white,
        zIndex: 2,
    },
    goalBox: {
        position: 'relative',
        maxHeight: 300,
        overflow: 'auto',
    },
    footer: {
        position: 'sticky',
        bottom: 0,
        background: theme.white,
        zIndex: 2,
    },
}));

// 목표 계층 구조 렌더링을 위한 하위 컴포넌트
const GoalTree = ({
    goals,
    currentGoalId,
    handleSelectGoal,
    subGoalItemClass,
}: GoalTreeProps) => {
    if (!goals || goals.length === 0) return null;

    // 계층 구조 맵 생성
    const goalMap = new Map<string, Partial<GoalWithInfos>>();
    const childrenMap = new Map<string | null, Partial<GoalWithInfos>[]>();

    // 모든 목표를 맵에 추가
    goals.forEach(goal => {
        if (!goal.id) return;
        goalMap.set(goal.id, goal);

        // 부모 ID를 기준으로 자식 목표 그룹화
        const parentId = goal.parent_id || null;
        if (!childrenMap.has(parentId)) {
            childrenMap.set(parentId, []);
        }
        childrenMap.get(parentId)?.push(goal);
    });

    // 최상위 목표 (부모가 없는 목표)
    const rootGoals = childrenMap.get(null) || [];

    // 부모 ID가 있지만 해당 부모가 숨김 처리되어, 불러온 목표 리스트 데이터에 없는 경우를 처리
    goals.forEach(goal => {
        if (!goal.id || !goal.parent_id) return;

        const parentId = goal.parent_id;
        if (parentId && !goalMap.has(parentId)) {
            // 부모가 맵에 없으면 최상위 목표로 처리
            rootGoals.push(goal);

            // 이미 childrenMap에서 이 목표를 제거 (중복 방지)
            const siblings = childrenMap.get(parentId) || [];
            const filteredSiblings = siblings.filter(
                sibling => sibling.id !== goal.id
            );
            if (filteredSiblings.length > 0) {
                childrenMap.set(parentId, filteredSiblings);
            } else {
                childrenMap.delete(parentId);
            }
        }
    });

    // 재귀적으로 목표 트리 렌더링
    const renderGoalTree = (
        goals: Partial<GoalWithInfos>[],
        level: number
    ): React.ReactNode => {
        return goals.map(goal => {
            if (!goal.id) return null;

            const hasChildren =
                childrenMap.has(goal.id) &&
                (childrenMap.get(goal.id)?.length || 0) > 0;
            const isSelected = goal.id === currentGoalId;

            return (
                <Box key={goal.id}>
                    <Button
                        color="blue"
                        size="sm"
                        fullWidth
                        className={level > 0 ? subGoalItemClass : ''}
                        p="xs"
                        value={goal.id}
                        variant="subtle"
                        onClick={e => {
                            e.stopPropagation();

                            if (isSelected) {
                                void handleSelectGoal(null);
                            } else {
                                void handleSelectGoal(goal.id);
                            }
                        }}
                        styles={{
                            root: {
                                display: 'flex',
                                justifyContent: 'start',
                            },
                        }}
                    >
                        <Group
                            justify="space-between"
                            w="100%"
                            style={{ overflow: 'hidden' }}
                        >
                            <Text
                                size="sm"
                                c="dark"
                                lineClamp={1}
                                flex={1}
                                style={{
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                }}
                            >
                                {goal.title}
                            </Text>
                            {isSelected && (
                                <IconCheck size={16} color="#228be6" />
                            )}
                        </Group>
                    </Button>

                    {hasChildren && (
                        <div>
                            {renderGoalTree(
                                childrenMap.get(goal.id) || [],
                                level + 1
                            )}
                        </div>
                    )}
                </Box>
            );
        });
    };

    return <>{renderGoalTree(rootGoals, 0)}</>;
};

// 프로젝트 아코디언 컴포넌트
const ProjectAccordion = ({
    projectGoals,
    activeProjectId,
    renderGoals,
}: ProjectAccordionProps) => {
    // 일반 프로젝트와 '기타' 프로젝트 분리
    const normalProjects = projectGoals.filter(
        ({ project }) => project.id !== 'others'
    );
    const othersProject = projectGoals.find(
        ({ project }) => project.id === 'others'
    );

    return (
        <Accordion
            variant="default"
            chevronPosition="left"
            defaultValue={activeProjectId}
            styles={{
                item: { maxWidth: '100%', width: '100%', overflow: 'hidden' },
                control: {
                    maxWidth: '100%',
                    width: '100%',
                    overflow: 'hidden',
                },
                panel: { maxWidth: '100%', width: '100%', overflow: 'hidden' },
                content: {
                    maxWidth: '100%',
                    width: '100%',
                    overflow: 'hidden',
                },
            }}
        >
            {/* 일반 프로젝트 렌더링 */}
            {normalProjects.map(({ project, goals }) => (
                <Accordion.Item key={project.id} value={project.id}>
                    <Accordion.Control>
                        <Group justify="space-between">
                            <Text size="xs" c="dimmed" fw={400}>
                                {project.name}
                            </Text>
                            <Pill size="xs">{goals.length}</Pill>
                        </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                        <Stack gap={0}>{renderGoals(goals)}</Stack>
                    </Accordion.Panel>
                </Accordion.Item>
            ))}

            {/* '기타' 카테고리 렌더링 */}
            {othersProject && (
                <Accordion.Item
                    key={othersProject.project.id}
                    value={othersProject.project.id}
                >
                    <Accordion.Control>
                        <Group justify="space-between">
                            <Text size="xs" c="dimmed" fw={400}>
                                {othersProject.project.name}
                            </Text>
                            <Pill size="xs">{othersProject.goals.length}</Pill>
                        </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                        <Stack gap={0}>
                            {renderGoals(othersProject.goals)}
                        </Stack>
                    </Accordion.Panel>
                </Accordion.Item>
            )}
        </Accordion>
    );
};

// 커스텀 훅: 목표 데이터 관리
const useGoalData = (
    menuOpened: boolean,
    viewMode: 'user' | 'all',
    goalId?: string | null,
    goalVisibilityFilter: GoalVisibilityFilter = GoalVisibilityFilter.VISIBLE
): {
    goals: Partial<GoalWithInfos>[];
    isLoading: boolean;
    user: UserWithDisplaySettings;
} => {
    const { user } = useAuthStore();
    const userId = user?.id;

    // 모달 상태에 따른 데이터 로드 최적화
    // goalId가 있거나 메뉴가 열려있을 때 데이터 로드
    const shouldLoadUserGoals = (!!goalId || menuOpened) && viewMode === 'user';
    const shouldLoadAllTeams = menuOpened && viewMode === 'all';

    // 사용자 목표 로드
    const { data: userGoals, isLoading: isUserGoalsLoading } = useUserGoals(
        userId,
        shouldLoadUserGoals,
        goalVisibilityFilter
    );

    // 사용자의 팀 데이터 로드
    const { data: myTeams, isLoading: isTeamsLoading } =
        useMyTeams(shouldLoadAllTeams);

    // 팀 목표 데이터 로드
    const { data: allTeamGoals, isLoading: isAllGoalsLoading } =
        useMultiTeamGoals(
            shouldLoadAllTeams ? (myTeams || []).map(team => team.id) : [],
            userId,
            shouldLoadAllTeams
        );

    const goals = useMemo(() => {
        // return viewMode === 'user' ? userGoals : allTeamGoals;
        const allGoals = [...(userGoals || []), ...(allTeamGoals || [])];

        const uniqueGoals = allGoals.reduce((acc, goal) => {
            if (goal?.id) {
                acc[goal.id] = goal; // 동일한 ID가 있으면 덮어씌움
            }
            return acc;
        }, {});

        return Object.values(uniqueGoals) as Partial<GoalWithInfos>[];
    }, [userGoals, allTeamGoals]);

    const isLoading = isUserGoalsLoading || isTeamsLoading || isAllGoalsLoading;

    return { goals, isLoading, user };
};

// 커스텀 훅: 프로젝트별 목표 그룹화
const useProjectGoals = (goals: Partial<GoalWithInfos>[] | undefined) => {
    return useMemo(() => {
        if (!goals || goals.length === 0) return [];

        const projectMap = new Map<
            string,
            { project: Project; goals: Partial<GoalWithInfos>[] }
        >();

        // '기타' 카테고리를 위한 고유 식별자
        const othersCategoryId = 'others';

        goals.forEach(goal => {
            if (!ActiveStatusCodes.includes(goal.status_code as StatusCode))
                return;

            if (!goal.project) {
                // 프로젝트가 없는 경우 '프로젝트 없음' 카테고리에 추가
                if (!projectMap.has(othersCategoryId)) {
                    projectMap.set(othersCategoryId, {
                        project: {
                            id: othersCategoryId,
                            name: t('project:placeholder'),
                            register_date: new Date().toISOString(),
                        } as Project,
                        goals: [],
                    });
                }
                projectMap.get(othersCategoryId)?.goals.push(goal);
                return;
            }

            // 프로젝트가 있는 경우 기존 로직대로 처리
            const projectId = goal.project.id;
            if (!projectMap.has(projectId)) {
                projectMap.set(projectId, {
                    project: {
                        id: goal.project.id,
                        name: goal.project.name,
                        register_date: goal.project.register_date,
                    } as Project,
                    goals: [],
                });
            }

            projectMap.get(projectId)?.goals.push(goal);
        });

        return Array.from(projectMap.values());
    }, [goals]);
};

// 메인 컴포넌트
export default function GoalSelector({
    todoId,
    goalId,
    customButton,
    goalVisibilityFilter = GoalVisibilityFilter.VISIBLE,
    onGoalChange,
    withinPortal = true,
}: GoalSelectorProps) {
    const { t } = useTranslation(['todo', 'project', 'common']);
    const { classes } = useStyles();
    const [menuOpened, setMenuOpened] = useState(false);
    const [viewMode, setViewMode] = useState<'user' | 'all'>('user');
    const { closeDrawer } = useTodoDrawerStore();

    // 데이터 로딩 커스텀 훅 사용
    const { goals, isLoading, user } = useGoalData(
        menuOpened,
        viewMode,
        goalId,
        goalVisibilityFilter
    );

    const updateTodo = useUpdateTodo();

    // 프로젝트별 목표 그룹화 커스텀 훅 사용
    const projectGoals = useProjectGoals(goals);

    // 현재 선택된 목표 정보
    // 목표 ID가 있지만 goals 배열에서 찾지 못할 경우 데이터베이스에서 직접 조회
    const localGoal = useMemo(() => {
        if (!goalId || !goals) return null;
        return goals.find(goal => goal.id === goalId);
    }, [goalId, goals]);

    // 로컬에서 찾지 못한 경우에만 DB에서 조회
    const { data: singleGoal, isLoading: isSingleGoalLoading } = useGoalById(
        goalId || '',
        {
            enabled: !!goalId && !localGoal,
        }
    );

    // 로컬에서 찾은 목표 또는 DB에서 가져온 목표 사용
    const currentGoal = localGoal || singleGoal || null;

    // 현재 선택된 goal_id가 속한 프로젝트 ID 찾기
    const activeProjectId = useMemo(() => {
        if (!goalId || !goals) return null;
        const selectedGoal = goals.find(goal => goal.id === goalId);
        return selectedGoal?.project?.id || null;
    }, [goalId, goals]);

    // 목표 선택 핸들러
    const handleSelectGoal = useCallback(
        async (goalId: string | null) => {
            const action = goalId
                ? t('todo:component.todoDrawer.goal_selector.actions.connect')
                : t(
                      'todo:component.todoDrawer.goal_selector.actions.disconnect'
                  );

            try {
                if (todoId) {
                    const loadingNotificationId = notifications.show({
                        title: t(
                            'todo:component.todoDrawer.goal_selector.notifications.processing_title',
                            { action }
                        ),
                        message: t('common:notification.message'),
                        loading: true,
                        autoClose: false,
                        withCloseButton: false,
                    });

                    // 선택된 목표가 있는 경우 team_id 확인
                    const selectedGoal = goalId
                        ? goals?.find(goal => goal.id === goalId)
                        : null;
                    const hasTeamId =
                        selectedGoal?.team_id !== undefined &&
                        selectedGoal?.team_id !== null;

                    await updateTodo.mutateAsync({
                        id: todoId,
                        goal_id: goalId,
                        update_id: user?.id ?? '',
                        ...(goalId !== null && hasTeamId
                            ? { is_public: true } // 팀과 연결하는 경우 비공개 불가
                            : {}),
                    });

                    notifications.update({
                        id: loadingNotificationId,
                        title: t(
                            'todo:component.todoDrawer.goal_selector.notifications.success_title',
                            { action }
                        ),
                        message: t(
                            'todo:component.todoDrawer.goal_selector.notifications.success_message',
                            { action }
                        ),
                        color: 'green',
                        loading: false,
                        autoClose: 3000,
                    });
                }
            } catch (error) {
                console.error('Failed to update todo goal:', error);
                notifications.show({
                    title: t(
                        'todo:component.todoDrawer.goal_selector.notifications.error_title',
                        { action }
                    ),
                    message: t(
                        'todo:component.todoDrawer.goal_selector.notifications.error_message',
                        { action }
                    ),
                    color: 'red',
                    loading: false,
                    autoClose: 3000,
                });
            }
            setMenuOpened(false);
            onGoalChange?.(goalId);
        },
        [todoId, updateTodo, user?.id, onGoalChange, goals, t]
    );

    // 메뉴 상태가 변경될 때 호출되는 핸들러
    const handleMenuChange = useCallback(
        (opened: boolean) => {
            setMenuOpened(opened);

            // todo 항목에 메뉴 열림 상태 속성 설정
            const item = document.querySelector(`[data-todo-id="${todoId}"]`);
            if (item) {
                item.setAttribute('data-menu-opened', opened.toString());
            }
        },
        [todoId]
    );

    // 현재 목표 정보를 표시하는 렌더링 함수
    const renderCurrentGoalInfo = () => {
        if (customButton)
            return (
                <Tooltip
                    label={t('component.todoDrawer.goal_selector.connect_goal')}
                    withArrow
                >
                    {customButton}
                </Tooltip>
            );

        if (currentGoal) {
            return (
                <Group gap="xs" wrap="nowrap">
                    <Text
                        size="xs"
                        truncate="end"
                        style={{ cursor: 'pointer' }}
                    >
                        {currentGoal.title}
                    </Text>
                    <Tooltip
                        label={t(
                            'component.todoDrawer.goal_selector.view_goal_details'
                        )}
                        withArrow
                    >
                        <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="gray"
                            component={Link}
                            to={`/goal/${currentGoal.id}`}
                            onClick={e => {
                                e.stopPropagation();
                                closeDrawer();
                            }}
                        >
                            <IconExternalLink size={12} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            );
        }

        return (
            <Button
                variant="subtle"
                rightSection={<IconSwipe size={14} />}
                size="compact-sm"
                color="gray"
                radius="md"
            >
                <Text size="xs">
                    {t('component.todoDrawer.goal_selector.please_select_goal')}
                </Text>
            </Button>
        );
    };

    // 목표 목록 렌더링 함수
    const renderGoalContent = () => {
        if (!goals || goals.length === 0) {
            return (
                <Menu.Item disabled>
                    {t('component.todoDrawer.goal_selector.no_goals')}
                </Menu.Item>
            );
        }

        if (isLoading) {
            return (
                <div style={{ padding: 16, textAlign: 'center' }}>
                    <Loader size="sm" />
                    <Text size="sm" mt={8}>
                        {t('component.todoDrawer.goal_selector.loading_goals')}
                    </Text>
                </div>
            );
        }

        if (projectGoals.length === 0) {
            return (
                <Menu.Item disabled>
                    {t('component.todoDrawer.goal_selector.no_goals')}
                </Menu.Item>
            );
        }

        return (
            <Box className={classes.goalBox} maw={280} w="100%">
                <ScrollArea.Autosize>
                    <ProjectAccordion
                        projectGoals={projectGoals}
                        activeProjectId={activeProjectId}
                        renderGoals={goalsData => (
                            <GoalTree
                                goals={goalsData || []}
                                currentGoalId={goalId}
                                handleSelectGoal={handleSelectGoal}
                                subGoalItemClass={classes.subGoalItem}
                            />
                        )}
                    />
                </ScrollArea.Autosize>
            </Box>
        );
    };

    return (
        <Menu
            opened={menuOpened}
            withinPortal={withinPortal}
            onChange={handleMenuChange}
            width={280}
        >
            <Menu.Target>{renderCurrentGoalInfo()}</Menu.Target>

            <Menu.Dropdown>
                <Box>
                    {viewMode === 'all' ? (
                        <Box className={classes.header}>
                            <Button
                                styles={{
                                    inner: { justifyContent: 'start' },
                                }}
                                color="gray"
                                variant="subtle"
                                size="sm"
                                leftSection={<IconChevronLeft size={16} />}
                                onClick={() => setViewMode('user')}
                                fullWidth
                            >
                                {t(
                                    'component.todoDrawer.goal_selector.go_back'
                                )}
                            </Button>
                        </Box>
                    ) : (
                        <Box className={classes.header} px="sm" py="4px">
                            <Group gap={4} c="gray">
                                <Icon name={ICONS.COMMON.PROJECT} />
                                <Text size="sm">
                                    {goals?.find(goal => goal.id === goalId)
                                        ?.title ??
                                        t(
                                            'component.todoDrawer.goal_selector.please_select_goal'
                                        )}
                                </Text>
                            </Group>
                        </Box>
                    )}

                    {renderGoalContent()}

                    {viewMode === 'user' && (
                        <Box className={classes.footer}>
                            <Button
                                styles={{
                                    inner: { justifyContent: 'start' },
                                }}
                                variant="subtle"
                                color="gray"
                                size="sm"
                                leftSection={<IconList size={16} />}
                                onClick={() => setViewMode('all')}
                                fullWidth
                            >
                                {t(
                                    'component.todoDrawer.goal_selector.view_all_goals'
                                )}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Menu.Dropdown>
        </Menu>
    );
}
